import { ReactNode, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MapPin,
  Truck,
  FileText,
  Warehouse,
  ShoppingCart,
  Package2,
  Users,
  BarChart3,
  Activity,
  Mail,
  Radio,
  TrendingUp,
  Receipt,
  ChevronDown,
  FileSignature,
  ClipboardList,
  HeartHandshake,
  Settings,
  DollarSign,
  Menu,
  X,
  AlertTriangle,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { PageTopBar } from './PageTopBar';
import { cn } from '@/lib/utils';
import { useActionItemCounts } from '@/components/dashboard/MyActionItems';
import { useCustomerOpsNavCounts } from '@/hooks/useCustomerOpsNavCounts';
import { useStorePulseAlertCounts } from '@/hooks/useStorePulseAlertCounts';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeKey?: 'posToReceive' | 'itemsToShip' | 'storePulseAlerts';
}

interface NavSection {
  title: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Sales & GTM',
    items: [
      { name: 'Leads', href: ROUTES.LEADS, icon: ClipboardList },
      { name: 'Deals', href: ROUTES.DEALS, icon: TrendingUp },
      { name: 'Estimates & Invoices', href: ROUTES.SALES_BILLING, icon: Receipt },
      { name: 'License Agreements', href: ROUTES.SLA_AGREEMENTS, icon: FileSignature },
      { name: 'Contacts', href: ROUTES.CONTACTS, icon: Users },
      { name: 'Network Pulse', href: ROUTES.NETWORK_PULSE, icon: Radio },
    ],
    defaultOpen: true,
  },
  {
    title: 'Operations',
    items: [
      { name: 'Locations', href: ROUTES.LOCATIONS, icon: MapPin },
      { name: 'Upcoming Launches', href: ROUTES.UPCOMING_LAUNCHES, icon: ShoppingCart },
      { name: 'Shipments', href: ROUTES.SHIPMENTS, icon: Truck, badgeKey: 'itemsToShip' },
      { name: 'Purchase Orders', href: ROUTES.PURCHASE_ORDERS, icon: FileText, badgeKey: 'posToReceive' },
    ],
    defaultOpen: true,
  },
  {
    title: 'Inventory',
    items: [
      { name: 'Inventory Management', href: ROUTES.INVENTORY_MANAGEMENT, icon: Warehouse },
      { name: 'Products & Pricing', href: ROUTES.PRODUCTS_PRICING, icon: Package2 },
    ],
    defaultOpen: true,
  },
  {
    title: 'Analytics',
    items: [
      { name: 'Reporting', href: ROUTES.REPORTING, icon: BarChart3 },
      { name: 'V2 Status', href: ROUTES.V2_STATUS, icon: Activity },
      { name: 'Email Insights', href: ROUTES.EMAIL_INSIGHTS, icon: Mail },
      { name: 'Store Pulse Alerts', href: ROUTES.STORE_PULSE_ALERTS, icon: AlertTriangle, badgeKey: 'storePulseAlerts' },
    ],
    defaultOpen: true,
  },
];

interface SidebarNavigationProps {
  badgeCounts: Record<string, number>;
  customerOpsBadge: number;
  isActive: (href: string) => boolean;
  onNavigate?: () => void;
  openSections: Record<string, boolean>;
  toggleSection: (title: string) => void;
}

function SidebarNavigation({
  badgeCounts,
  customerOpsBadge,
  isActive,
  onNavigate,
  openSections,
  toggleSection,
}: SidebarNavigationProps) {
  const primaryItems: NavItem[] = [
    { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: LayoutDashboard },
    { name: 'Customer Ops', href: ROUTES.CUSTOMER_OPS, icon: HeartHandshake },
    { name: 'Finance', href: ROUTES.CASHFLOW, icon: DollarSign },
    { name: 'Settings', href: ROUTES.SETTINGS, icon: Settings },
  ];

  return (
    <nav className="flex-1 space-y-2 overflow-y-auto p-3 lg:p-4" aria-label="Primary navigation">
      {primaryItems.map((item) => {
        const Icon = item.icon;
        const showCustomerOpsBadge = item.href === ROUTES.CUSTOMER_OPS && customerOpsBadge > 0;
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={onNavigate}
            aria-current={isActive(item.href) ? 'page' : undefined}
            className={cn(
              'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive(item.href)
                ? 'bg-[#5ac59a] text-gray-950'
                : 'text-white hover:bg-[#5ac59a] hover:text-gray-950'
            )}
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="flex-1">{item.name}</span>
            {showCustomerOpsBadge ? (
              <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1 text-xs font-bold text-white">
                {customerOpsBadge > 99 ? '99+' : customerOpsBadge}
              </span>
            ) : null}
          </Link>
        );
      })}

      <div className="space-y-2">
        {NAV_SECTIONS.map((section) => {
          const isOpen = openSections[section.title];
          const hasActiveItem = section.items.some((item) => isActive(item.href));

          return (
            <div key={section.title} className="space-y-1">
              <button
                type="button"
                onClick={() => toggleSection(section.title)}
                className={cn(
                  'flex min-h-11 w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
                  hasActiveItem
                    ? 'bg-[#5ac59a]/10 text-[#5ac59a]'
                    : 'text-gray-400 hover:text-gray-300'
                )}
                aria-expanded={isOpen}
              >
                <span>{section.title}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
              </button>

              {isOpen ? (
                <div className="ml-2 space-y-1 border-l-2 border-gray-700 pl-2">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] ?? 0 : 0;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={onNavigate}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          active
                            ? 'bg-[#5ac59a] text-gray-950'
                            : 'text-gray-300 hover:bg-[#5ac59a] hover:text-gray-950'
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        <span className="flex-1">{item.name}</span>
                        {badgeCount > 0 ? (
                          <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white">
                            {badgeCount > 9 ? '9+' : badgeCount}
                          </span>
                        ) : null}
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

function SidebarLogo({ compact = false }: { compact?: boolean }) {
  return (
    <img
      src="/tpb-logo.png"
      alt="The Peak Beyond Logo"
      className={cn('w-auto object-contain', compact ? 'h-10' : 'h-24')}
    />
  );
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { posToReceive, itemsToShip } = useActionItemCounts();
  const { sidebarBadge: customerOpsBadge } = useCustomerOpsNavCounts();
  const { count: storePulseAlerts } = useStorePulseAlertCounts();

  // Badge counts mapping
  const badgeCounts: Record<string, number> = {
    posToReceive,
    itemsToShip,
    storePulseAlerts,
  };
  
  // Initialize section open states
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    NAV_SECTIONS.forEach((section) => {
      state[section.title] = section.defaultOpen ?? true;
    });
    return state;
  });

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const isActive = (href: string) => {
    if (href === ROUTES.DASHBOARD) {
      return location.pathname === ROUTES.DASHBOARD;
    }
    if (href === ROUTES.CUSTOMER_OPS) {
      return location.pathname === ROUTES.CUSTOMER_OPS || location.pathname.startsWith(`${ROUTES.CUSTOMER_OPS}/`);
    }
    if (href === ROUTES.CASHFLOW) {
      return location.pathname === ROUTES.CASHFLOW || location.pathname.startsWith(`${ROUTES.CASHFLOW}/`);
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50 xl:flex">
      <a
        href="#main-content"
        className="fixed left-4 top-4 z-[100] -translate-y-24 rounded-md bg-white px-4 py-3 font-semibold text-gray-950 shadow-lg ring-2 ring-gray-950 transition-transform focus:translate-y-0 motion-reduce:transition-none print:hidden"
      >
        Skip to main content
      </a>
      <header
        data-app-mobile-header
        className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-gray-800 bg-[#181818] px-4 print:hidden xl:hidden"
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ac59a] focus-visible:ring-offset-2 focus-visible:ring-offset-[#181818]"
          aria-label="Open navigation"
          aria-expanded={mobileNavOpen}
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link
          to={ROUTES.DASHBOARD}
          aria-label="Go to dashboard"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ac59a]"
        >
          <SidebarLogo compact />
        </Link>
        <span className="w-11" aria-hidden="true" />
      </header>

      <DialogPrimitive.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 xl:hidden" />
          <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-[51] flex w-[min(20rem,88vw)] flex-col border-r border-gray-700 bg-[#181818] shadow-2xl outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left xl:hidden">
            <DialogPrimitive.Title className="sr-only">Navigation menu</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Main application navigation
            </DialogPrimitive.Description>
            <div className="flex h-20 shrink-0 items-center justify-between border-b border-gray-700 px-5">
              <SidebarLogo compact />
              <DialogPrimitive.Close className="inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ac59a]" aria-label="Close navigation">
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>
            <SidebarNavigation
              badgeCounts={badgeCounts}
              customerOpsBadge={customerOpsBadge}
              isActive={isActive}
              onNavigate={() => setMobileNavOpen(false)}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-gray-200 bg-[#181818] print:hidden xl:flex">
        <div className="flex h-44 shrink-0 items-center justify-center border-b border-gray-700 p-6">
          <SidebarLogo />
        </div>
        <SidebarNavigation
          badgeCounts={badgeCounts}
          customerOpsBadge={customerOpsBadge}
          isActive={isActive}
          openSections={openSections}
          toggleSection={toggleSection}
        />
      </aside>

      <main
        id="main-content"
        tabIndex={-1}
        aria-label="Main content"
        className="min-w-0 flex-1 overflow-x-hidden p-4 focus:outline-none sm:p-6 xl:p-8 print:p-0"
      >
        <PageTopBar />
        {children}
      </main>
    </div>
  );
}
